import { useMemo, type ReactElement } from 'react';
import { AddFeatureDialog } from '@/features/features/components/dialogs';
import { useFeaturesPageViewModel } from '@/features/features/hooks/useFeaturesPageViewModel';
import {
  featureModeTabsSx,
  featureModeTabSx,
  resolveFeaturesStatusCardIcon,
} from '@/features/features/pages/featureModePresentation';
import { FeaturesTablePanel } from '@/features/features/pages/FeaturesTablePanel';
import { PageLoadingFallback } from '@/features/metrics/components/PageLoadingFallback';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import { DeliveryListEmptyState } from '@/features/shared/components/DeliveryListEmptyState';
import { SearchTagsDateHeaderFiltersNext } from '@/features/shared/components/entityTrends/SearchTagsDateHeaderFiltersNext';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { s } from '@/theme/tokens';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { AddIcon, Box, Button, FeaturesIcon, Tab, Tabs, Text } from '@/ui/primitives';
import type { FeatureStatus } from '@flowqa/shared';

export const FeaturesPage = (): ReactElement => {
  const {
    activeMode,
    setActiveMode,
    modeCounts,
    searchValue,
    setSearchValue,
    selectedStatuses,
    selectedTags,
    selectedProjects,
    tagOptions,
    tagOptionsLoading,
    statusOptions,
    projectFilterOptions,
    showProjectFilter,
    onStatusesChange,
    onTagsChange,
    onProjectsChange,
    tableFilters,
    featureMetricCards,
    featureMetricCardsLoading,
    showPageLoading,
    isKnownEmpty,
    addDialog,
  } = useFeaturesPageViewModel();
  const tableProjectOptions = useMemo(
    () =>
      projectFilterOptions.map((option) => ({
        id: option.value,
        name: option.label,
        badgeChars: '',
        badgeColor: 'neutral' as const,
        badgeEnabled: false,
      })),
    [projectFilterOptions],
  );
  const tableNoRowsText = getEmptyStateMessage('entity.noneFound', { entity: 'feature' });

  if (showPageLoading) {
    return <PageLoadingFallback loadingMessageKey="features" />;
  }

  return (
    <>
      <ListPageTemplate
        headerActionsMode="separate"
        title={
          <Text role="pageTitle" component="h1">
            Features
          </Text>
        }
        titleActions={
          isKnownEmpty ? null : (
            <Button
              variant="primary"
              fullWidthOnMobile={false}
              startIcon={<AddIcon />}
              onClick={addDialog.onOpen}
            >
              Add Feature
            </Button>
          )
        }
        headerActions={
          isKnownEmpty ? null : (
            <SearchTagsDateHeaderFiltersNext
              searchAriaLabel="Search features"
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              selectedStatuses={selectedStatuses}
              statusOptions={statusOptions}
              onStatusesChange={(next) => {
                onStatusesChange(next as FeatureStatus[]);
              }}
              selectedTags={selectedTags}
              tagOptions={tagOptions}
              tagOptionsLoading={tagOptionsLoading}
              onTagsChange={onTagsChange}
              selectedProjects={showProjectFilter ? selectedProjects : undefined}
              onProjectsChange={showProjectFilter ? onProjectsChange : undefined}
              projectOptions={showProjectFilter ? projectFilterOptions : undefined}
              range="7d"
              onRangeChange={() => undefined}
              pickerStartDay={null}
              pickerEndDay={null}
              hasCustomDates={false}
              isLatestMode={false}
              onSelectedStartDayChange={() => undefined}
              onSelectedEndDayChange={() => undefined}
              includeLatest={false}
              showDateFilter={false}
              showCustomDateRange={false}
            />
          )
        }
        summary={
          isKnownEmpty ? null : (
            <StatusMetricCardsRow
              cards={featureMetricCards}
              loading={featureMetricCardsLoading}
              resolveStatusCardIcon={resolveFeaturesStatusCardIcon}
            />
          )
        }
        content={
          isKnownEmpty ? (
            <DeliveryListEmptyState
              actionLabel="Add Feature"
              body="Create your first feature to track planned work, release readiness and related test health."
              icon={FeaturesIcon}
              title={getEmptyStateMessage('entity.noneYetForProject', { entity: 'feature' })}
              onActionClick={addDialog.onOpen}
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: s('lg') }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={activeMode}
                  onChange={(_, nextMode) => {
                    setActiveMode(nextMode as typeof activeMode);
                  }}
                  aria-label="Feature delivery mode"
                  preserveScrollOnChange={false}
                  sx={featureModeTabsSx}
                >
                  <Tab
                    label={`In Progress (${modeCounts.inProgress})`}
                    value="inProgress"
                    sx={featureModeTabSx}
                  />
                  <Tab
                    label={`Released (${modeCounts.released})`}
                    value="released"
                    sx={featureModeTabSx}
                  />
                </Tabs>
              </Box>
              <FeaturesTablePanel
                featureMode={activeMode}
                fixedFilters={tableFilters}
                noRowsText={tableNoRowsText}
                projectOptions={tableProjectOptions}
                searchValue={searchValue}
                tagOptions={tagOptions}
              />
            </Box>
          )
        }
        hideHeader={isKnownEmpty}
      />
      <AddFeatureDialog
        open={addDialog.open}
        title="Add Feature"
        saving={addDialog.saving}
        values={addDialog.values}
        errors={addDialog.errors}
        statusOptions={statusOptions}
        projectOptions={addDialog.projectOptions}
        releaseOptions={addDialog.releaseOptions}
        showProjectSelect={addDialog.showProjectSelect}
        releaseDisabled={addDialog.releaseDisabled}
        errorMessage={addDialog.errorMessage}
        onClose={addDialog.onClose}
        onSave={addDialog.onSave}
        handlers={addDialog.handlers}
      />
    </>
  );
};
